import { formatDistanceToNow } from "date-fns";
import { Briefcase, ExternalLink, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useLawyerEngagementRequests, useReviewLawyerEngagementRequest } from "@/hooks/use-supabase-queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function LawyerRequestsCard() {
  const { data: requests, isLoading } = useLawyerEngagementRequests();
  const reviewRequest = useReviewLawyerEngagementRequest();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const pendingRequests = (requests || []).filter((request: any) => request.status === "pending");

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Client Instructions</CardTitle>
          <p className="text-sm text-muted-foreground">{pendingRequests.length} waiting for your review</p>
        </div>
        {pendingRequests.length > 0 ? (
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            New
          </Badge>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {pendingRequests.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No client instructions waiting right now.</p>
        ) : (
          pendingRequests.map((request: any) => {
            const requesterName = `${request.requester?.first_name || ""} ${request.requester?.last_name || ""}`.trim() || "PropTRUST user";
            const propertyLabel = request.property?.title || "Property request";

            return (
              <div key={request.id} className="space-y-3 rounded-2xl border border-border bg-muted/20 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-foreground">{propertyLabel}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {requesterName} · {(request.requester?.role || "client").replace("-", " ")}
                    </p>
                    {request.property?.address ? (
                      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{request.property.address}, {request.property.state}</span>
                      </div>
                    ) : null}
                  </div>
                  <Badge variant="secondary" className="bg-lawyer/10 text-lawyer">
                    <Briefcase className="mr-1 h-3.5 w-3.5" />
                    Review
                  </Badge>
                </div>

                <p className="rounded-xl bg-background px-3 py-2 text-sm leading-6 text-muted-foreground">
                  {request.request_message || "No extra note was included with this request."}
                </p>

                {request.property ? (
                  <div className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
                    <p className="font-medium text-foreground">Property details</p>
                    <p className="mt-1 text-muted-foreground">{request.property.address}, {request.property.state}</p>
                    <p className="mt-1 text-primary">Price: {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(request.property.price || 0)}</p>
                    {request.property.address ? (
                      <Button variant="outline" size="sm" className="mt-2" asChild>
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${request.property.address}, ${request.property.state || ""}`)}`} target="_blank" rel="noreferrer">
                          <ExternalLink className="mr-2 h-4 w-4" />
                          View on Map
                        </a>
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span>{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</span>
                  <span>{request.requester?.organization_name || "Independent participant"}</span>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    className="flex-1"
                    onClick={async () => {
                      try {
                        await reviewRequest.mutateAsync({ requestId: request.id, status: "accepted" });
                        toast.success("Request accepted", {
                          description: "The requester has been notified and can continue inside PropTRUST.",
                        });
                      } catch (error) {
                        toast.error("Could not accept request", {
                          description: error instanceof Error ? error.message : "Please try again.",
                        });
                      }
                    }}
                  >
                    Accept request
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={async () => {
                      try {
                        await reviewRequest.mutateAsync({ requestId: request.id, status: "rejected" });
                        toast.success("Request declined");
                      } catch (error) {
                        toast.error("Could not decline request", {
                          description: error instanceof Error ? error.message : "Please try again.",
                        });
                      }
                    }}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
